import { FC, useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import { IRoutingMachineProps, RoutesFoundEvent } from "./types";
import L, { LatLngExpression } from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-routing-machine";
import { isValidWaypoints } from "./constants";

const RoutingMachine: FC<IRoutingMachineProps> = ({
  srcAddress,
  dstAddress,
}) => {
  const map = useMap();
  const routingControlRef = useRef<any>(null);

  const waypoints: LatLngExpression[] = srcAddress
    ? [
        L.latLng(srcAddress.lat, srcAddress.lng),
        ...dstAddress.map((dst) => L.latLng(dst.lat, dst.lng)),
      ]
    : [];

  const safeRemoveControl = () => {
    try {
      if (routingControlRef.current && map) {
        routingControlRef.current.off("routesfound");

        const plan = routingControlRef.current.getPlan();
        if (plan) {
          plan.setWaypoints([]);
        }

        map.removeControl(routingControlRef.current);

        routingControlRef.current = null;
      }
    } catch (e) {
      console.error("Error removing routing control:", e);
      routingControlRef.current = null;
    }
  };

  useEffect(() => {
    if (waypoints.length < 2 || !isValidWaypoints(waypoints)) {
      safeRemoveControl();
      return;
    }

    safeRemoveControl();

    try {
      const options = {
        waypoints,
        routeWhileDragging: false,
        lineOptions: { styles: [{ weight: 4 }] },
        addWaypoints: false,
        draggableWaypoints: false,
        fitSelectedRoutes: false,
        showAlternatives: false,
      };

      routingControlRef.current = (L as any).Routing.control(options).addTo(
        map,
      );

      if (routingControlRef.current) {
        routingControlRef.current.on(
          "routesfound",
          function (e: RoutesFoundEvent) {
            const route = e.routes[0];
            if (route) {
              const bounds = L.latLngBounds(route.coordinates);
              map.flyToBounds(bounds, {
                paddingTopLeft: [400, 50],
                paddingBottomRight: [50, 50],
                duration: 2,
                easeLinearity: 0.25,
              });
            }
          },
        );
      }
    } catch (e) {
      console.error("Error creating routing control:", e);
      routingControlRef.current = null;
    }

    return () => {
      safeRemoveControl();
    };
  }, [srcAddress, dstAddress, map]);

  useEffect(() => {
    if (srcAddress && srcAddress.lat) {
      map.flyTo([srcAddress.lat, srcAddress.lng], 15, {
        animate: true,
        duration: 2,
        easeLinearity: 0.25,
      });
    }
  }, [srcAddress, map]);

  useEffect(() => {
    const validDestinations = dstAddress.filter(
      (dst) => dst.lat !== 0 && dst.lng !== 0,
    );

    if (validDestinations.length > 0) {
      const lastDestination = validDestinations[validDestinations.length - 1];

      if (lastDestination.lat !== 0 && lastDestination.lng !== 0) {
        const currentCenter = map.getCenter();
        const lastLatLng = L.latLng(lastDestination.lat, lastDestination.lng);

        if (currentCenter.distanceTo(lastLatLng) > 10) {
          map.flyTo([lastDestination.lat, lastDestination.lng], 15, {
            animate: true,
            duration: 2,
            easeLinearity: 0.25,
          });
        }
      }
    }
  }, [dstAddress, map]);

  return null;
};

export default RoutingMachine;
